import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon/icon-constants';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { getOrgSubdomainLengthLimit, isOrgsCleanUpEnabled, } from '@thoughtspot/session-service';
import React, { useCallback, useRef, useState } from 'react';
import { useCanDeleteOrgMutation, useCreateOrgMutation, useDeleteOrgMutation, useGetOrgsListQuery, useUpdateOrgMutation, } from "../../../../services/org/org-service";
import { getErrorCode } from "../../../user-management/util";
import { BasePageContext } from "../../../../contexts/basepage-context";
import { getErrorObjectFromGraphQLErrors } from "../../../../utils/error-handling/error-utils";
import { create } from '../../../../utils/logger';
import { BaseComponentContainer } from '../../base-component/base-component.container';
import { CreateEditModal } from '../../create-edit-modal/create-edit-modal.container';
import { DeleteConfirmationModal } from '../../delete-confirmation-modal/delete-confirmation-modal';
import { ListContainer } from '../../listcontainer/list.container';
import { TranslationsKeys } from '../../orgs-interface';
import { SearchBar } from '../../search-bar/search-bar';
import styles from './org.module.scss';
import { useDataHooks } from './useDataHook';
import { getColumnDefinitions, ListActionType, orgParams, sortKeyMap, tableColumnHeaders, } from './util';
const logger = create('orgs.index.container');
const MODE = {
    UPDATE: 'UPDATE',
    ADD: 'ADD',
    DELETE: 'DELETE',
};
const ERROR_CODE = {
    10002: 10002,
    10003: 10003,
    10081: 10081,
    10082: 10082,
};
export const Org = () => {
    const { t } = useTranslation();
    const { metadataService, metadataList } = BasePageContext();
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [orgData, setOrgData] = useState({});
    const [isRefresh, setIsRefresh] = useState(false);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [showError, setShowError] = useState(null);
    const [mode, setMode] = useState(null);
    const [orgListInput, setOrgListInput] = useState({ ...orgParams });
    const [isLoading, setIsLoading] = useState(false);
    const [canOrgBeDeleted, setCanOrgBeDeleted] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog,] = useState(false);
    const sortData = useRef({
        sort: orgParams.sort,
        sortAscending: orgParams.sortascending,
    });
    const { data, error, loading, isLastBatch } = useDataHooks(useGetOrgsListQuery, isRefresh, orgListInput, setIsLoading);
    const getToastMessage = (scenario) => {
        if (scenario === MODE.ADD) {
            return t(TranslationsKeys.ADD_ORG_SUCCESS_MSG);
        }
        if (scenario === MODE.UPDATE) {
            return t(TranslationsKeys.UPDATE_ORG_SUCCESS_MSG);
        }
        if (scenario === MODE.DELETE) {
            return t(TranslationsKeys.DELETE_ORG_SUCCESS_MSG);
        }
        return null;
    };
    const onChangeOffset = (offset) => {
        const { sort, sortAscending } = sortData.current;
        setOrgListInput({
            ...orgListInput,
            offset,
            sort,
            sortascending: sortAscending,
        });
        setIsLoading(true);
    };
    const onSuccessDeleteOrg = (isDeleted) => {
        if (isDeleted) {
            const { sort, sortAscending } = sortData.current;
            setOrgListInput({
                ...orgListInput,
                offset: 0,
                sort,
                sortascending: sortAscending,
            });
            setIsRefresh(true);
        }
    };
    const [deleteOrg, deleteOrgObj] = useDeleteOrgMutation(onSuccessDeleteOrg, err => { }, orgData.id);
    const { data: createOrgData, error: createOrgError, loading: createOrgLoading, createOrg, } = useCreateOrgMutation();
    const { data: updateOrgData, error: updateOrgError, loading: updateOrgLoading, updateOrg, } = useUpdateOrgMutation();
    const { data: canDeleteOrgData, error: canDeleteOrgError, loading: canDeleteOrgLoading, canDeleteOrg, } = useCanDeleteOrgMutation();
    const noDataProps = {
        title: t('NO_MATCH_MSG'),
        description: '',
        img: ErrorCode.GLOBAL_DATA,
    };
    const toggleCreateModal = () => {
        if (isModalOpened) {
            setIsRefresh(true);
        }
        setIsModalOpened(!isModalOpened);
        setIsEditMode(false);
        setShowError(null);
        setOrgData({});
    };
    const logMixPanelEvent = (userActionType, userActionObject = {}) => {
        const userAction = new UserAction(userActionType);
        collectEvent(userAction, {
            timestamp: Date.now(),
            ...userActionObject,
        });
    };
    const updateOrgRecord = (orgRecord) => {
        updateOrg(orgRecord)
            .then(res => {
            setIsToastAlertVisible(true);
            setMode(MODE.UPDATE);
            toggleCreateModal();
            setIsRefresh(false);
            logMixPanelEvent(userActionTypeEnum.ORG_UPDATE_SUCCESS);
            return '';
        })
            .catch(err => {
            const errorCode = getErrorCode(getErrorObjectFromGraphQLErrors(err));
            if (errorCode === ERROR_CODE[10003]) {
                setShowError(t('msg_code.10003.summary'));
                logMixPanelEvent(userActionTypeEnum.ORG_UPDATE_FAILURE, {
                    errorMessage: t('msg_code.10003.summary'),
                });
            }
            else if (errorCode === ERROR_CODE[10081]) {
                setShowError(t(TranslationsKeys.ORG_NAME_INVALID));
                logMixPanelEvent(userActionTypeEnum.ORG_UPDATE_FAILURE, {
                    errorMessage: t(TranslationsKeys.ORG_NAME_INVALID),
                });
            }
            else {
                setShowError(t(TranslationsKeys.ORG_NAME_ALREADY_EXISTS));
                logMixPanelEvent(userActionTypeEnum.ORG_UPDATE_FAILURE, {
                    errorMessage: t(TranslationsKeys.ORG_NAME_ALREADY_EXISTS),
                });
            }
        });
    };
    const createOrgRecord = (orgRecord) => {
        createOrg(orgRecord)
            .then(res => {
            setIsToastAlertVisible(true);
            setMode(MODE.ADD);
            toggleCreateModal();
            setIsRefresh(false);
            logMixPanelEvent(userActionTypeEnum.ORG_ADD_SUCCESS);
            let originRef = window.location !== window.parent.location
                ? document.referrer
                : document.location.origin;
            originRef = originRef || '*';
            window.parent.postMessage({ messageType: 'orgsListUpdated' }, originRef);
            return '';
        })
            .catch(err => {
            const errorCode = getErrorCode(getErrorObjectFromGraphQLErrors(err));
            if (errorCode === ERROR_CODE[10003]) {
                setShowError(t('msg_code.10003.summary'));
                logMixPanelEvent(userActionTypeEnum.ORG_ADD_FAILURE, {
                    errorMessage: t('msg_code.10003.summary'),
                });
            }
            else if (errorCode === ERROR_CODE[10081]) {
                setShowError(t(TranslationsKeys.ORG_NAME_INVALID));
                logMixPanelEvent(userActionTypeEnum.ORG_ADD_FAILURE, {
                    errorMessage: t(TranslationsKeys.ORG_NAME_INVALID),
                });
            }
            else if (errorCode === ERROR_CODE[10082]) {
                const error = t('accountActivation.sessionExpiry', {
                    1: getOrgSubdomainLengthLimit(),
                });
                setShowError(error);
                logMixPanelEvent(userActionTypeEnum.ORG_ADD_FAILURE, {
                    errorMessage: error,
                });
            }
            else {
                setShowError(t(TranslationsKeys.ORG_NAME_ALREADY_EXISTS));
                logMixPanelEvent(userActionTypeEnum.ORG_ADD_FAILURE, {
                    errorMessage: t(TranslationsKeys.ORG_NAME_ALREADY_EXISTS),
                });
            }
        });
    };
    const useSuppressNavigation = () => {
        const countRef = useRef(-1);
        const suppressNavigation = useCallback((modalProps) => {
            const isMoreOptionsColumn = modalProps.colDef.colId === tableColumnHeaders.MORE_OPTION;
            if (!isMoreOptionsColumn) {
                countRef.current = -1;
                return false;
            }
            if (countRef.current === -1) {
                countRef.current =
                    modalProps.event.srcElement.lastChild.childElementCount + 1;
            }
            countRef.current--;
            return countRef.current !== 0;
        }, []);
        return suppressNavigation;
    };
    const suppressNavigation = useSuppressNavigation();
    const onRowActionCB = ({ action, data: actionData }) => {
        setOrgData(actionData);
        if (action === ListActionType.EDIT) {
            setIsEditMode(true);
            setIsModalOpened(!isModalOpened);
            logMixPanelEvent(userActionTypeEnum.ORG_UPDATE);
        }
        else if (action === ListActionType.DELETE) {
            if (isOrgsCleanUpEnabled()) {
                setShowDeleteConfirmationDialog(true);
                setCanOrgBeDeleted(true);
                logMixPanelEvent(userActionTypeEnum.ORG_DELETE);
            }
            else {
                canDeleteOrg(actionData.id)
                    .then(res => {
                    const { allowDelete } = res?.data?.Org_canDelete?.Data;
                    setShowDeleteConfirmationDialog(true);
                    setCanOrgBeDeleted(allowDelete);
                    logMixPanelEvent(userActionTypeEnum.ORG_DELETE);
                    return '';
                })
                    .catch(err => {
                    logger.error(err);
                });
            }
        }
    };
    const onDeleteConfirm = async () => {
        await deleteOrg()
            .then(res => {
            setShowDeleteConfirmationDialog(false);
            setIsToastAlertVisible(true);
            setMode(MODE.DELETE);
            setIsRefresh(false);
            logMixPanelEvent(userActionTypeEnum.ORG_DELETE_SUCCESS);
            let originRef = window.location !== window.parent.location
                ? document.referrer
                : document.location.origin;
            originRef = originRef || '*';
            window.parent.postMessage({ messageType: 'orgsListUpdated' }, originRef);
            return '';
        })
            .catch(err => {
            logMixPanelEvent(userActionTypeEnum.ORG_DELETE_FAILURE);
        });
    };
    const onClickAddOrgBtn = () => {
        toggleCreateModal();
        logMixPanelEvent(userActionTypeEnum.ORG_ADD);
    };
    const renderCreateOrgBtn = () => (React.createElement(Button, { icon: IconID.PLUS, text: t(TranslationsKeys.ADD_ORG_BTN_LABEL), onClick: onClickAddOrgBtn, type: "primary" }));
    const onSearchChange = (term) => {
        const { sort, sortAscending } = sortData.current;
        setOrgListInput({
            ...orgListInput,
            sort,
            offset: 0,
            sortascending: sortAscending,
            pattern: term,
        });
        setIsLoading(true);
    };
    const onSortingChanged = (sortState) => {
        const sortingColumn = sortState.find((el) => {
            return el.sort !== 'null';
        }) || sortState[0];
        const sortKey = (sortingColumn &&
            sortKeyMap[sortingColumn.colId]) ||
            'DEFAULT';
        const ascending = sortingColumn ? sortingColumn.sort === 'asc' : true;
        sortData.current.sort = sortKey;
        sortData.current.sortAscending = ascending;
        setOrgListInput({
            ...orgListInput,
            sort: sortKey,
            offset: 0,
            sortascending: ascending,
        });
        setIsLoading(true);
    };
    const listViewProps = {
        data,
        columns: getColumnDefinitions(),
        options: {
            suppressMovable: true,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
        },
        onSortingChanged,
        loading,
        suppressKeyboardEvent: suppressNavigation,
    };
    return (React.createElement(BaseComponentContainer, { id: "orgs-org-page", container: "orgContainer", title: t('orgs.organization'), renderActionButton: renderCreateOrgBtn },
        React.createElement(Vertical, null,
            React.createElement(LoadingIndicator.Global, { loadingText: "Loading", className: styles.container, hideLoader: !isLoading },
                React.createElement(Vertical, null,
                    React.createElement(Horizontal, { className: styles.gridSearch },
                        React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                                onSearchChange(searchTerm);
                            }, "data-testid": "search-bar-filters" })),
                    React.createElement(ListContainer, { testId: "org-orgs-page", data: data, listViewProps: listViewProps, noDataProps: noDataProps, offSet: orgListInput.offset, paginationSize: orgListInput.batchsize, isLastBatch: isLastBatch, onChangeOffset: onChangeOffset }, metadataList)),
                isModalOpened && (React.createElement(CreateEditModal, { isModalOpened: isModalOpened, closeModal: () => {
                        setIsModalOpened(!isModalOpened);
                        setIsEditMode(false);
                        setShowError(null);
                        setOrgData({});
                    }, isEditMode: isEditMode, orgId: orgData.id, setIsRefresh: setIsRefresh, updateOrgRecord: updateOrgRecord, createOrgRecord: createOrgRecord, showError: showError, setShowError: setShowError, setIsLoading: setIsLoading })),
                showDeleteConfirmationDialog && (React.createElement(DeleteConfirmationModal, { title: t(TranslationsKeys.DELETE_ORG_MODAL_TITLE), name: orgData.orgName.text, onDeleteModalClose: () => {
                        setShowDeleteConfirmationDialog(false);
                    }, onDeleteConfirm: onDeleteConfirm, showLoader: deleteOrgObj.loading, canDeleteOrg: canOrgBeDeleted }))),
            React.createElement(GlobalAlert.Toast, { message: getToastMessage(mode), isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true }))));
};
//# sourceMappingURL=index.container.js.map